import React, { useState, useEffect } from "react";
import { compose, withAppContext, withActions } from "containers/HOC";
import Grid from "components/Grid";
import Page from "components/Page";
import PageBody from "components/PageBody";
import FarmHomePageHead from "./FarmHomePageHead";
import { Redirect, withRouter } from "react-router";
import Analyses from "./Widgets/Analyses";
import Access from "./Widgets/Access";
import Activity from "./Widgets/Activity";
import Subscriptions from "./Widgets/Subscriptions";
import Trends from "./Widgets/Trends";
import LatestAnalysis from "./Widgets/LatestAnalysis";
import Benchmarks from "./Widgets/Benchmarks";
import FarmGroups from "./Widgets/FarmGroups";
import GridCell from "components/GridCell";
import { fetchBudget } from "containers/Budget/_actions";
import * as utils from "common/utils";
import { fetchFarmAccess } from "../Farm/_actions";
import { useEmailTracking } from "common/effects";

const FarmDashboard = ({ farm = {}, refData, match, fetchBudget, fetchFarmAccess, online }) => {
    const displayPayment = match.path.endsWith("dp");
    const [fetchIds, setFetchIds] = useState([]);
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const { deploymentType = "Commercial" } = refData;
    const isCommercial = deploymentType === "Commercial";
    const hasFarmAccess = utils.canUpdateFarm(farm);
    const isGuest = farm.role === "Guest";
    useEmailTracking("Farm dashboard opened", farm.name);

    useEffect(() => {
        hasFarmAccess && farm.id && fetchFarmAccess(farm.id);
    }, [hasFarmAccess, farm.id, fetchFarmAccess]);

    useEffect(() => {
        let isMounted = true;
        const isFetching = (farm.offlineBudgets || []).some((ob) => ob.isFetching);
        isMounted && setAnalysisLoading(isFetching);
        return () => (isMounted = false);
    }, [farm]);

    const getDefaultId = () => {
        const yearEndBudgets = (farm.offlineBudgets && farm.offlineBudgets.filter((b) => b.budget.class === "Budget" && (b.budget.type === "YearEnd" || b.budget.type === "PrivateYearEnd") && b.budget.role === "Write").sort((a, b) => b.budget.year - a.budget.year)) || [];
        const yearEndPublications = (farm.offlineBudgets && farm.offlineBudgets.filter((b) => b.budget.class === "Budget" && (b.budget.type === "YearEnd" || b.budget.type === "PrivateYearEnd") && b.budget.role === "Write").sort((a, b) => b.budget.year - a.budget.year)) || [];
        let latestToCompare = yearEndBudgets && yearEndBudgets.length > 0 ? yearEndBudgets[0] : yearEndPublications && yearEndPublications.length > 0 && yearEndPublications;
        if (!latestToCompare && farm.offlineBudgets && farm.offlineBudgets.length > 0) latestToCompare = farm.offlineBudgets.find((o) => o.budget.role === "Write");
        if (latestToCompare) {
            if (!latestToCompare.isDownloaded && !fetchIds.includes(latestToCompare.id)) {
                setFetchIds([...fetchIds, latestToCompare.id]);
                fetchBudget(farm.id, latestToCompare.id, false);
            }
            return latestToCompare.id;
        }
        return latestToCompare ? latestToCompare.id : undefined;
    };

    const [defaultId, setDefaultId] = useState(getDefaultId());
    const { offlineBudgets = [] } = farm;
    const offlineBudget = defaultId && offlineBudgets.find((b) => b.id === defaultId);
    const analysisToCompare = offlineBudget && offlineBudget.isDownloaded ? offlineBudget.budget : undefined;
    const canUpdateFarm = utils.canUpdateFarm(farm);

    const changeAnalysis = (id) => {
        const inBudget = offlineBudgets.find((b) => b.id === id);
        if (inBudget && !inBudget.isDownloaded && !fetchIds.includes(inBudget.id)) {
            setFetchIds([...fetchIds, inBudget.id]);
            fetchBudget(farm.id, inBudget.id, false);
        }
        setDefaultId(id);
    };

    if (refData.deploymentType !== "Science" && canUpdateFarm && (!farm.latitude || !farm.longitude)) return <Redirect to={`/app/farm/${farm.id}/settings`} />;

    if (!farm.offlineBudgets || farm.offlineBudgets.filter((o) => o.budget && o.budget.role !== "Header").length === 0) {
        return <Redirect to={`/app/farm/${farm.id}/analysis`} />;
    }

    const breadcrumb = [{ name: farm.displayName }];

    return (
        <Page>
            <FarmHomePageHead farm={farm} refData={refData} displayPayment={displayPayment} breadcrumb={breadcrumb} />
            <PageBody>
                <Grid>
                    <GridCell className="u-xl-width2of5 u-md-width1of1">
                        <Grid>
                            <Analyses farm={farm} className="u-width1of1" />
                            {canUpdateFarm && isCommercial && <Subscriptions farm={farm} refData={refData} className="u-width1of1" />}
                            {!isGuest && <Trends farm={farm} className="u-width1of1" />}
                            {canUpdateFarm && isCommercial && (
                                <>
                                    <Access farm={farm} className="u-width1of1" />
                                    <FarmGroups farm={farm} className="u-width1of1" />
                                </>
                            )}
                        </Grid>
                    </GridCell>
                    <GridCell className="u-xl-width3of5 u-md-width1of1 u-pl-0 u-pr-0">
                        <LatestAnalysis farm={farm} className="u-width1of1" analysisToCompare={analysisToCompare} changeAnalysis={changeAnalysis} analysisLoading={analysisLoading} online={online} />
                        {canUpdateFarm && (
                            <>
                                {isCommercial && <Benchmarks farm={farm} className="u-width1of1" analysisToCompare={analysisToCompare} changeAnalysis={changeAnalysis} analysisLoading={analysisLoading} />}
                                <Activity farm={farm} className="u-width1of1" />
                            </>
                        )}
                    </GridCell>
                </Grid>
            </PageBody>
        </Page>
    );
};

export default compose(withAppContext, withRouter, withActions({ fetchBudget, fetchFarmAccess }))(FarmDashboard);
